import { useScienceTutorSection } from './scienceTutorContext';
import { ArrowLeft, CheckCircle2, CircleAlert, FlaskConical, ShieldCheck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ACID_REACTIONS_PROVENANCE,
  ACID_REACTIONS_TOOL_IDENTITY,
  ACIDS,
  GAS_TEST_DETAILS,
  GAS_TEST_LABELS,
  GAS_TEST_OPTIONS,
  PARTNER_KIND_LABELS,
  PARTNERS,
  PATTERN_LABELS,
  PATTERN_OPTIONS,
  SALT_NAMING_RULES,
  SALT_OPTIONS,
  acidById,
  balancedEquation,
  checkGasTestPrediction,
  checkPatternPrediction,
  checkSaltPrediction,
  gasTestFor,
  lookupReaction,
  partnerById,
  saltName,
  saltOptionLabel,
  wordEquation,
  type AcidId,
  type AcidReaction,
  type GasTestId,
  type IndicatorStage,
  type PartnerId,
  type PartnerKind,
  type PredictionFeedback,
  type ProductPattern,
  type SaltOptionId,
} from './acidReactions';
import { AcidGasExplorer } from './AcidGasExplorer';
import { AtomCheck, BeakerSchematic, IndicatorPanel } from './AcidReactionsVisuals';
import { formatSpecies } from './reactionPredictionData';
import { StudyShell } from './StudyShell';

const PARTNER_KINDS = Object.keys(PARTNER_KIND_LABELS) as PartnerKind[];

function choiceClass(selected: boolean) {
  return `rounded-2xl border px-3 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
    selected ? 'border-brand-coral bg-wash-coral text-ink' : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
  }`;
}

function FeedbackBox({ label, result }: { label: string; result: PredictionFeedback }) {
  return (
    <div
      role="region"
      aria-label={label}
      className={`rounded-2xl border p-4 ${result.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'}`}
    >
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        {result.correct ? (
          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
        )}
        {result.correct ? 'Correct' : 'Not quite'}
      </p>
      <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
    </div>
  );
}

function SaltQuestion({ reaction }: { reaction: AcidReaction }) {
  const [choice, setChoice] = useState<SaltOptionId | ''>('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('SaltQuestion', { choice, checked });
  const result = checked && choice ? checkSaltPrediction(reaction, choice) : null;
  return (
    <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
      <p className="text-sm font-extrabold text-ink">Name the salt</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-2" role="group" aria-label="Name the salt">
        {SALT_OPTIONS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={choice === option}
            onClick={() => {
              setChoice(option);
              setChecked(false);
            }}
            className={choiceClass(choice === option)}
          >
            {saltOptionLabel(reaction, option)}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={!choice}
        onClick={() => setChecked(true)}
        className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        Check salt name
      </button>
      {result ? (
        <div className="mt-3">
          <FeedbackBox label="Salt name feedback" result={result} />
        </div>
      ) : null}
    </div>
  );
}

function ModelReveal({
  reaction,
  stage,
  onStageChange,
}: {
  reaction: AcidReaction;
  stage: IndicatorStage;
  onStageChange: (stage: IndicatorStage) => void;
}) {
  const acid = acidById(reaction.acid);
  const gasTest = GAS_TEST_DETAILS[gasTestFor(reaction.pattern)];
  const salt = saltName(reaction);
  return (
    <section aria-label="Model result" className="space-y-4">
      <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Word equation</p>
        <p className="mt-1 text-base font-extrabold leading-7 text-ink">{wordEquation(reaction)}</p>
        <p className="mt-4 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Balanced equation</p>
        <p className="mt-1 break-words text-base font-extrabold leading-7 text-ink" aria-label="Balanced equation">
          {balancedEquation(reaction)}
        </p>
        {reaction.pattern !== 'no-reaction' ? (
          <div className="mt-3">
            <AtomCheck reaction={reaction} />
          </div>
        ) : null}
        {reaction.note ? <p className="mt-3 text-sm font-medium leading-6 text-ink-soft">{reaction.note}</p> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">What you would see</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
            {reaction.observations.map((observation) => (
              <li key={observation}>{observation}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Gas test</p>
          <p className="mt-1 text-sm font-extrabold text-ink">{gasTest.gas}</p>
          <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{gasTest.method}</p>
          <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{gasTest.result}</p>
        </div>
      </div>

      {salt ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-[1.5rem] border border-hairline bg-wash-sky p-4 sm:p-5">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">Salt naming rule</p>
            <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">
              {SALT_NAMING_RULES[acid.saltFamily]} The first part of the name comes from the metal in{' '}
              {partnerById(reaction.partner).name}.
            </p>
          </div>
          <SaltQuestion key={reaction.id} reaction={reaction} />
        </div>
      ) : null}

      <IndicatorPanel reaction={reaction} stage={stage} onStageChange={onStageChange} />
    </section>
  );
}

export function AcidReactionsLabPage() {
  const [acidId, setAcidId] = useState<AcidId>('HCl');
  const [partnerId, setPartnerId] = useState<PartnerId>('Mg');
  const [pattern, setPattern] = useState<ProductPattern | ''>('');
  const [gasTest, setGasTest] = useState<GasTestId | ''>('');
  const [revealed, setRevealed] = useState(false);
  const [stage, setStage] = useState<IndicatorStage>('start');
  const lookup = lookupReaction(acidId, partnerId);
  const reaction = lookup.status === 'reviewed' ? lookup.reaction : null;
  const acid = acidById(acidId);
  const partner = partnerById(partnerId);

  const reset = () => {
    setPattern('');
    setGasTest('');
    setRevealed(false);
    setStage('start');
  };

  const reveal = () => {
    setRevealed(true);
    setStage(reaction && reaction.pattern !== 'no-reaction' ? 'used-up' : 'start');
  };

  const patternResult = revealed && reaction && pattern ? checkPatternPrediction(reaction, pattern) : null;
  const gasResult = revealed && reaction && gasTest ? checkGasTestPrediction(reaction, gasTest) : null;

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'acid-reactions', state: { acidId, partnerId, pattern, gasTest, stage, answer_revealed: revealed } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-coral/25 bg-gradient-to-br from-wash-coral via-canvas-pure to-wash-sky p-6 shadow-card-soft lg:p-8"
        data-tool-key={ACID_REACTIONS_TOOL_IDENTITY.toolKey}
        data-tool-version={ACID_REACTIONS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-coral">
              <FlaskConical className="h-4 w-4" aria-hidden="true" /> Acids, ions and electrochemistry
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Acid Reactions Lab</h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Choose an acid and something to add. Predict what forms and how to test the gas, then check the
              equations, the salt name and the indicator colours. Then work out how much gas a reaction makes.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            From Year 9 · explore only, not mastery evidence
          </div>
        </div>
      </header>

      <div className="my-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <section aria-label="Choose reactants" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">1. Choose an acid</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-3" role="group" aria-label="Choose an acid">
            {ACIDS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={acidId === option.id}
                aria-label={option.name}
                onClick={() => {
                  setAcidId(option.id);
                  reset();
                }}
                className={choiceClass(acidId === option.id)}
              >
                <span className="block">{formatSpecies(option.species)}</span>
                <span className="block text-xs font-bold text-slate2">{option.name}</span>
              </button>
            ))}
          </div>

          <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">2. Choose what to add</p>
          <div className="mt-3 space-y-3">
            {PARTNER_KINDS.map((kind) => (
              <div key={kind}>
                <p className="text-xs font-extrabold text-slate2">{PARTNER_KIND_LABELS[kind]}</p>
                <div className="mt-1.5 grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label={PARTNER_KIND_LABELS[kind]}>
                  {PARTNERS.filter((option) => option.kind === kind).map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      aria-pressed={partnerId === option.id}
                      aria-label={option.name}
                      onClick={() => {
                        setPartnerId(option.id);
                        reset();
                      }}
                      className={choiceClass(partnerId === option.id)}
                    >
                      <span className="block">{formatSpecies(option.species)}</span>
                      <span className="block text-xs font-bold text-slate2">{option.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section aria-label="Predict and check" className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <h2 className="text-xl font-extrabold text-ink">
            {partner.name.charAt(0).toUpperCase() + partner.name.slice(1)} + {acid.name}
          </h2>
          <p className="mt-1 text-sm font-medium text-slate2">
            {partner.appearance} added to dilute {acid.name}.
          </p>

          {lookup.status === 'unsupported' ? (
            <div role="alert" className="mt-4 flex gap-3 rounded-2xl border border-brand-coral/45 bg-wash-coral p-4">
              <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-brand-coral" aria-hidden="true" />
              <div>
                <p className="text-sm font-extrabold text-ink">Not modelled: the simple pattern does not apply</p>
                <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{lookup.reason}</p>
              </div>
            </div>
          ) : null}

          {reaction ? (
            <div className="mt-4 space-y-5">
              <BeakerSchematic reaction={reaction} revealed={revealed} stage={stage} />

              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">3. Predict the products</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label="Predict the products">
                  {PATTERN_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={pattern === option}
                      onClick={() => {
                        setPattern(option);
                        setRevealed(false);
                      }}
                      className={choiceClass(pattern === option)}
                    >
                      {PATTERN_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">4. Predict the gas test</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2" role="group" aria-label="Predict the gas test">
                  {GAS_TEST_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={gasTest === option}
                      onClick={() => {
                        setGasTest(option);
                        setRevealed(false);
                      }}
                      className={choiceClass(gasTest === option)}
                    >
                      {GAS_TEST_LABELS[option]}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="button"
                disabled={!pattern || !gasTest}
                onClick={reveal}
                className="rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Mix and check
              </button>

              {patternResult && gasResult ? (
                <div className="grid gap-3 lg:grid-cols-2">
                  <FeedbackBox label="Products feedback" result={patternResult} />
                  <FeedbackBox label="Gas test feedback" result={gasResult} />
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>

      {reaction && revealed ? <ModelReveal reaction={reaction} stage={stage} onStageChange={setStage} /> : null}

      <AcidGasExplorer />

      <footer className="my-6 space-y-2 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Acids and alkalis are corrosive. Do practical work only with teacher supervision, eye protection and your
          school’s risk assessment. This browser model is not a safety guide.
        </p>
        <p>
          Model boundaries: the salt solution is treated as neutral (pH about 7) once the acid is used up; some metal
          salt solutions, such as copper(II) and iron(II) salts, are slightly acidic. Litmus is simplified to red,
          purple or blue; universal indicator shades vary by brand. Conditions: {ACID_REACTIONS_PROVENANCE.conditions}
        </p>
        <p>
          Sources: {ACID_REACTIONS_PROVENANCE.sources.join(' · ')}. Reviewed teaching cases (pending teaching review).
          This tool does not save an attempt, record mastery or claim alignment to a state syllabus.
        </p>
      </footer>
    </StudyShell>
  );
}
